/**
 * App-level notifications for finished jobs and discoveries (OA-220).
 *
 * The API's realtime socket (api #566, OA-56) sends the owner of a job or
 * discovery job a `notification` frame when it completes or fails, without
 * any subscription:
 *
 *   { "type": "notification", "id": "…", "ts": "…",
 *     "data": { "kind": "job.completed|job.failed|discovery.completed|discovery.failed",
 *               "jobId": "…" | "discoveryJobId": "…", "label": "report.pdf" } }
 *
 * This module is the seam between that socket and the UI. It does not open a
 * connection itself: a transport registers here with
 * `setNotificationTransport`, and `subscribeNotifications` (used by
 * NotificationRegion) asks the transport for frames only while something is
 * listening. Until a transport is registered, nothing is delivered and
 * `isNotificationTransportAvailable()` is false, so NotificationRegion renders
 * nothing.
 *
 * The realtime client (src/services/realtime.ts, OA-56) registers itself as
 * the transport when it loads, unless realtime is turned off. It hands
 * `notification` frames through untouched and keeps the socket open while
 * NotificationRegion listens; NotificationRegion imports it for that side
 * effect, since it is otherwise only loaded with the job and discovery pages.
 *
 * Validation stays here (parseNotificationFrame), so the client passes frames
 * through untouched.
 */

export type NotificationKind =
  | 'job.completed'
  | 'job.failed'
  | 'discovery.completed'
  | 'discovery.failed';

export interface AppNotification {
  kind: NotificationKind;
  /** `job` for the job kinds, `discovery` for the discovery kinds. */
  resource: 'job' | 'discovery';
  /** The job id or discovery job id. */
  resourceId: string;
  /** File name, discovery title or domain, as the API sent it; may be absent. */
  label: string | null;
  /** When the server sent it, if it said. */
  ts: string | null;
}

export type NotificationListener = (notification: AppNotification) => void;

/**
 * Something that can deliver raw `notification` frames: the realtime client.
 */
export interface NotificationTransport {
  /**
   * Start delivering frames to `onFrame`, connecting as needed.
   *
   * @returns Function that stops delivering them
   */
  listen: (onFrame: (frame: unknown) => void) => () => void;
}

const KINDS = new Set<NotificationKind>([
  'job.completed',
  'job.failed',
  'discovery.completed',
  'discovery.failed',
]);

// The API's id format (docs/REALTIME.md). Anything else is not linked to.
const ID_PATTERN = /^[\w-]{1,64}$/;

const MAX_LABEL_LENGTH = 200;

/**
 * Validate a frame from the socket and reduce it to what the UI shows.
 *
 * @param {unknown} frame - A parsed JSON frame
 * @returns {AppNotification | null} The notification, or null when the frame is not a usable notification
 */
export function parseNotificationFrame(frame: unknown): AppNotification | null {
  if (!frame || typeof frame !== 'object') {
    return null;
  }
  const { type, ts, data } = frame as { type?: unknown; ts?: unknown; data?: unknown };
  if (type !== 'notification' || !data || typeof data !== 'object') {
    return null;
  }
  const { kind, jobId, discoveryJobId, label } = data as Record<string, unknown>;
  if (typeof kind !== 'string' || !KINDS.has(kind as NotificationKind)) {
    return null;
  }
  const resource = kind.startsWith('job.') ? 'job' : 'discovery';
  const resourceId = resource === 'job' ? jobId : discoveryJobId;
  if (typeof resourceId !== 'string' || !ID_PATTERN.test(resourceId)) {
    return null;
  }
  const trimmed = typeof label === 'string' ? label.trim().slice(0, MAX_LABEL_LENGTH) : '';
  return {
    kind: kind as NotificationKind,
    resource,
    resourceId,
    label: trimmed || null,
    ts: typeof ts === 'string' ? ts : null,
  };
}

let transport: NotificationTransport | null = null;
let stopTransport: (() => void) | null = null;
const listeners = new Set<NotificationListener>();
const availabilityListeners = new Set<() => void>();

function deliver(frame: unknown): void {
  const notification = parseNotificationFrame(frame);
  if (!notification) {
    return;
  }
  for (const listener of listeners) {
    listener(notification);
  }
}

function startTransport(): void {
  if (transport && !stopTransport && listeners.size > 0) {
    stopTransport = transport.listen(deliver);
  }
}

function stopTransportIfStarted(): void {
  stopTransport?.();
  stopTransport = null;
}

/**
 * Register (or, with null, remove) the transport that delivers frames.
 *
 * @param {NotificationTransport | null} next - The transport, or null
 */
export function setNotificationTransport(next: NotificationTransport | null): void {
  if (next === transport) {
    return;
  }
  stopTransportIfStarted();
  transport = next;
  startTransport();
  for (const listener of availabilityListeners) {
    listener();
  }
}

/**
 * Whether a transport is registered, i.e. whether notifications can arrive.
 *
 * @returns {boolean} True once the realtime client has registered itself
 */
export function isNotificationTransportAvailable(): boolean {
  return transport !== null;
}

/**
 * Listen for changes to `isNotificationTransportAvailable()`.
 *
 * @param {() => void} listener - Called when a transport is registered or removed
 * @returns {() => void} Unsubscribe function
 */
export function onNotificationTransportChange(listener: () => void): () => void {
  availabilityListeners.add(listener);
  return () => {
    availabilityListeners.delete(listener);
  };
}

/**
 * Receive notifications. The transport is asked for frames while at least one
 * listener exists, and released when the last one goes.
 *
 * @param {NotificationListener} listener - Called with each valid notification
 * @returns {() => void} Unsubscribe function
 */
export function subscribeNotifications(listener: NotificationListener): () => void {
  listeners.add(listener);
  startTransport();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      stopTransportIfStarted();
    }
  };
}
